import { useState, useMemo } from 'react'

import { useCoordinator, UUID } from '@builddocs/data-management'
import { useAssigneesProjectQuery } from '@builddocs/filestore-api'
import { useDisclosure } from '@builddocs/system'
import { LoadingButton } from '@mui/lab'
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Skeleton,
  Stack,
  Box,
} from '@mui/material'
import _ from 'lodash'

import RepresentationsRegistryApplication from './RepresentationsRegistryApplication'
import TableForSelect from './TableForSelect'

type OwnProps = {
  initialRepresentatives: Record<string, UUID[]> | null
  onEditList: (reps: Record<string, UUID[]>) => void
}
const EditAssigneesDialog = ({
  initialRepresentatives,
  onEditList,
}: OwnProps) => {
  const { isOpen, onClose, onOpen } = useDisclosure()
  const { projectId } = useCoordinator() as { projectId: string }
  const { data: assignees = [], isLoading } =
    useAssigneesProjectQuery(projectId)
  const [selected, setSelected] = useState<Record<string, UUID[]>>(
    initialRepresentatives ?? {},
  )
  const [selectedKey, setSelectedKey] = useState<string>(
    'representative_designer_sign',
  )

  const handleSelect = (key, employmentId: UUID | UUID[]) => {
    if (Array.isArray(employmentId)) {
      setSelected(prev => ({ ...prev, [key]: employmentId }))
    }
  }
  const hasValue = useMemo(() => {
    if (
      initialRepresentatives === null &&
      !_.isEmpty(selected) &&
      Object.values(selected).flat().length > 0
    ) {
      return true
    }
    return (
      initialRepresentatives !== null &&
      !_.isEqual(selected, initialRepresentatives)
    )
  }, [initialRepresentatives, selected])
  const handleSave = () => {
    onEditList(selected)
    onClose()
  }

  const handleClose = () => {
    onClose()
    setSelected(initialRepresentatives ?? {})
  }

  return (
    <>
      <Button variant="tonal" onClick={onOpen}>
        Изменить состав комиссии реестра
      </Button>
      <Dialog
        fullWidth
        maxWidth="lg"
        open={isOpen}
        scroll="paper"
        onClose={handleClose}
      >
        <DialogTitle>Редактирование списка представителей реестра </DialogTitle>
        <DialogContent sx={{ overflow: 'hidden' }}>
          <DialogContentText>
            Выберите необходимых представителей из представленных участников
            проекта. Выбранные лица будут указаны в составе комиссии Реестра
            приложений.
          </DialogContentText>
          <Stack
            direction="row"
            spacing={2}
            sx={{ overflowY: 'auto', height: '73vh', mt: 2 }}
          >
            <Box
              sx={{
                position: 'sticky',
                display: 'flex',
                top: 0,
                flexDirection: 'column',
                overflowY: 'auto',
                overflowX: 'hidden',
                gap: 1,
                pr: 1,
              }}
            >
              <RepresentationsRegistryApplication
                assignees={assignees}
                selected={selected}
                selectedKey={selectedKey}
                onChangeKey={setSelectedKey}
              />
            </Box>
            <Box height="100%">
              {isLoading && <Skeleton />}
              {assignees && (
                <TableForSelect
                  items={assignees.reduce(
                    (acc, person) => ({
                      ...acc,
                      [person.employmentId]: person,
                    }),
                    {},
                  )}
                  selected={selected[selectedKey] ?? []}
                  tableType="assignees"
                  onSelect={list => handleSelect(selectedKey, list)}
                />
              )}
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Отмена</Button>
          <LoadingButton
            disabled={!hasValue}
            variant="contained"
            onClick={handleSave}
          >
            Сохранить
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </>
  )
}

export default EditAssigneesDialog
